import React, { useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { useTranslation } from "../hooks/useTranslation";
import { useLanguage } from "../components/language/LanguageContext";
import LanguageSelector from "../components/language/LanguageSelector";
import "./LandingPage.css";

const LandingPage = () => {
  const history = useHistory();
  const t = useTranslation();
  const { language } = useLanguage();
  const [billingCycle, setBillingCycle] = useState("monthly"); // monthly | yearly

  const toggleBilling = () => {
    setBillingCycle(billingCycle === "monthly" ? "yearly" : "monthly");
  };

  const handleDemoClick = () => {
    history.push("/demo");
  };

  return (
    <div className="lp-container">
      {/* SECTION 1: HERO */}
      <nav className="lp-nav">
        <div className="lp-logo">
          <img
            src={process.env.PUBLIC_URL + "/images/stoiccredit-logo.png"}
            alt="StoicCredit"
            width="40"
            height="40"
            loading="lazy"
            style={{ width: 40, height: 40, borderRadius: "50%" }}
          />
          StoicCredit
        </div>
        <div className="lp-nav-links">
          <LanguageSelector />
          <Link to="/login" className="lp-signin-btn">
            {t("landingPageContent.nav.signIn")}
          </Link>
        </div>
      </nav>

      <header className="lp-hero">
        <div className="lp-hero-content">
          <div className="lp-hero-text">
            <h1 className="lp-hero-title">
              {t("landingPageContent.hero.title")}
            </h1>
            <p className="lp-hero-subtitle">
              {t("landingPageContent.hero.subtitle")}
            </p>
            <ul className="lp-hero-features">
              <li>{t("landingPageContent.hero.feature1")}</li>
              <li>{t("landingPageContent.hero.feature2")}</li>
              <li>{t("landingPageContent.hero.feature3")}</li>
              <li>{t("landingPageContent.hero.feature4")}</li>
            </ul>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                alignItems: "flex-start",
              }}
            >
              <button onClick={handleDemoClick} className="lp-cta-button">
                {t("landingPageContent.hero.ctaButton")}
              </button>
              <div className="lp-hero-meta">
                <span>
                  {t("landingPageContent.hero.meta1")}
                </span>
                <span>{t("landingPageContent.hero.meta2")}</span>
              </div>
            </div>
          </div>

          <div className="lp-hero-visual">
            <div className="lp-split-visual">
              <div className="lp-split-left">
                <img
                  src={
                    process.env.PUBLIC_URL + "/images/lp-hero-split-left.png"
                  }
                  alt={t("landingPageContent.hero.altUpload")}
                  loading="lazy"
                  width="290"
                  height="300"
                />
              </div>
              <div className="lp-split-right">
                <img
                  src={
                    process.env.PUBLIC_URL + "/images/lp-hero-split-right.png"
                  }
                  alt={t("landingPageContent.hero.altPlan")}
                  loading="lazy"
                  width="290"
                  height="300"
                />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* SECTION 1.5: DEMO VIDEO */}
      <section className="lp-section lp-section-video">
        <div className="lp-video-container">
          <h2 className="lp-section-title">{t("landingPageContent.demoVideo.title")}</h2>
          <p className="lp-video-subtitle">{t("landingPageContent.demoVideo.subtitle")}</p>
          <div className="lp-video-wrapper">
            <iframe
              className="lp-video-embed"
              src={language === "es" 
                ? "https://www.youtube.com/embed/M0HA0nTKMIc" 
                : "https://www.youtube.com/embed/ZlrERds3pCM"}
              title={t("landingPageContent.demoVideo.title")}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      </section>

      {/* SECTION 2: THE PROBLEM */}
      <section className="lp-section lp-section-alt">
        <div className="lp-problem-box">
          <h2 className="lp-section-title">
            {t("landingPageContent.problem.title")}
          </h2>

          <div className="lp-problem-grid">
            <div>
              <div className="lp-problem-item">
                <span className="lp-problem-icon">❌</span>
                <div>
                  <strong>{t("landingPageContent.problem.problem1Title")}</strong>
                  <br />
                  {t("landingPageContent.problem.problem1Subtitle")}
                </div>
              </div>
              <div className="lp-problem-item">
                <span className="lp-problem-icon">❌</span>
                <div>
                  <strong>{t("landingPageContent.problem.problem2Title")}</strong>
                  <br />
                  {t("landingPageContent.problem.problem2Subtitle")}
                </div>
              </div>
              <div className="lp-problem-item">
                <span className="lp-problem-icon">❌</span>
                <div>
                  <strong>{t("landingPageContent.problem.problem3Title")}</strong>
                  <br />
                  {t("landingPageContent.problem.problem3Subtitle")}
                </div>
              </div>
            </div>

            <div className="lp-problem-divider">
              <p>{t("landingPageContent.problem.genericAdvice")}</p>
              <ul className="lp-data-list">
                <li> {t("landingPageContent.problem.data1")}</li>
                <li> {t("landingPageContent.problem.data2")}</li>
                <li> {t("landingPageContent.problem.data3")}</li>
                <li> {t("landingPageContent.problem.data4")}</li>
                <li> {t("landingPageContent.problem.data5")}</li>
              </ul>
              <div className="lp-data-highlight">
                <h4>{t("landingPageContent.problem.highlight")}</h4>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: THE MAGIC MOMENT */}
      <section className="lp-section">
        <h2 className="lp-section-title">
          {t("landingPageContent.howItWorks.title")}
        </h2>
        <div className="lp-steps-grid">
          <div className="lp-step-card">
            <div className="lp-step-number">1</div>
            <h3 className="lp-step-title">{t("landingPageContent.howItWorks.step1Title")}</h3>
            <p className="lp-step-desc">
              {t("landingPageContent.howItWorks.step1Desc")}
            </p>
            <div className="lp-step-visual">
              <img
                src={process.env.PUBLIC_URL + "/images/lp-step-1.png"}
                alt={t("landingPageContent.howItWorks.step1Alt")}
                loading="lazy"
                width="300"
                height="150"
              />
            </div>
            <ul className="lp-features-list">
              <li>
                <span className="lp-check">✓</span> {t("landingPageContent.howItWorks.step1Feature1")}
              </li>
              <li>
                <span className="lp-check">✓</span> {t("landingPageContent.howItWorks.step1Feature2")}
              </li>
              <li>
                <span className="lp-check">✓</span> {t("landingPageContent.howItWorks.step1Feature3")}
              </li>
            </ul>
          </div>

          <div className="lp-step-card">
            <div className="lp-step-number">2</div>
            <h3 className="lp-step-title">{t("landingPageContent.howItWorks.step2Title")}</h3>
            <p className="lp-step-desc">
              {t("landingPageContent.howItWorks.step2Desc")}
            </p>
            <div className="lp-step-visual">
              <img
                src={process.env.PUBLIC_URL + "/images/lp-step-2.png"}
                alt={t("landingPageContent.howItWorks.step2Alt")}
                loading="lazy"
                width="300"
                height="150"
              />
            </div>
            <ul className="lp-features-list">
              <li>
                <span className="lp-check">✓</span> {t("landingPageContent.howItWorks.step2Feature1")}
              </li>
              <li>
                <span className="lp-check">✓</span> {t("landingPageContent.howItWorks.step2Feature2")}
              </li>
            </ul>
          </div>

          <div className="lp-step-card">
            <div className="lp-step-number">3</div>
            <h3 className="lp-step-title">{t("landingPageContent.howItWorks.step3Title")}</h3>
            <p className="lp-step-desc">
              {t("landingPageContent.howItWorks.step3Desc")}
            </p>
            <div className="lp-step-visual">
              <img
                src={process.env.PUBLIC_URL + "/images/lp-step-3.png"}
                alt={t("landingPageContent.howItWorks.step3Alt")}
                loading="lazy"
                width="300"
                height="150"
              />
            </div>
            <ul className="lp-features-list">
              <li>
                <span className="lp-check">✓</span> {t("landingPageContent.howItWorks.step3Feature1")}
              </li>
              <li>
                <span className="lp-check">✓</span> {t("landingPageContent.howItWorks.step3Feature2")}
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* SECTION 4: DIFFERENTIATION */}
      <section className="lp-section lp-section-alt">
        <h2 className="lp-section-title">{t("landingPageContent.differentiation.title")}</h2>
        <table className="lp-comparison-table">
          <thead>
            <tr>
              <th>{t("landingPageContent.differentiation.tableHeader1")}</th>
              <th>{t("landingPageContent.differentiation.tableHeader2")}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{t("landingPageContent.differentiation.row1Col1")}</td>
              <td>{t("landingPageContent.differentiation.row1Col2")}</td>
            </tr>
            <tr>
              <td>{t("landingPageContent.differentiation.row2Col1")}</td>
              <td>{t("landingPageContent.differentiation.row2Col2")}</td>
            </tr>
            <tr>
              <td>{t("landingPageContent.differentiation.row3Col1")}</td>
              <td>{t("landingPageContent.differentiation.row3Col2")}</td>
            </tr>
            <tr>
              <td>{t("landingPageContent.differentiation.row4Col1")}</td>
              <td>{t("landingPageContent.differentiation.row4Col2")}</td>
            </tr>
            <tr>
              <td>{t("landingPageContent.differentiation.row5Col1")}</td>
              <td>{t("landingPageContent.differentiation.row5Col2")}</td>
            </tr>
          </tbody>
        </table>
        <div className="lp-diff-key">
          <h3>{t("landingPageContent.differentiation.keyTitle")}</h3>
          <p>
            {t("landingPageContent.differentiation.keyText")}
          </p>
        </div>
      </section>

      {/* SECTION 5: FEATURES */}
      <section className="lp-section">
        <h2 className="lp-section-title">{t("landingPageContent.features.title")}</h2>
        <div className="lp-feature-grid">
          <div className="lp-feature-card">
            <div className="lp-feature-content">
              <div className="lp-feature-title">{t("landingPageContent.features.feature1Title")}</div>
              <p>
                {t("landingPageContent.features.feature1Desc")}
              </p>
            </div>
            <div className="lp-feature-img">
              <img
                src={process.env.PUBLIC_URL + "/images/lp-feature-analyzer.png"}
                alt={t("landingPageContent.features.feature1Alt")}
                loading="lazy"
                width="600"
                height="200"
              />
            </div>
          </div>

          <div className="lp-feature-card">
            <div className="lp-feature-content">
              <div className="lp-feature-title">{t("landingPageContent.features.feature2Title")}</div>
              <p>
                {t("landingPageContent.features.feature2Desc")}
              </p>
            </div>
            <div className="lp-feature-img">
              <img
                src={process.env.PUBLIC_URL + "/images/lp-feature-plan.png"}
                alt={t("landingPageContent.features.feature2Alt")}
                loading="lazy"
                width="600"
                height="200"
              />
            </div>
          </div>

          <div className="lp-feature-card">
            <div className="lp-feature-content">
              <div className="lp-feature-title">{t("landingPageContent.features.feature3Title")}</div>
              <p>
                {t("landingPageContent.features.feature3Desc")}
              </p>
            </div>
            <div className="lp-feature-img">
              <img
                src={
                  process.env.PUBLIC_URL + "/images/lp-feature-inventory.png"
                }
                alt={t("landingPageContent.features.feature3Alt")}
                loading="lazy"
                width="600"
                height="200"
              />
            </div>
          </div>

          <div className="lp-feature-card">
            <div className="lp-feature-content">
              <div className="lp-feature-title">{t("landingPageContent.features.feature4Title")}</div>
              <p>
                {t("landingPageContent.features.feature4Desc")}
              </p>
            </div>
            {/* Knowledge Base image */}
            <div className="lp-feature-img">
              <img
                src={
                  process.env.PUBLIC_URL + "/images/lp-feature-knowledge.png"
                }
                alt={t("landingPageContent.features.feature4Alt")}
                loading="lazy"
                width="600"
                height="200"
              />
            </div>
          </div>
        </div>

        <div className="lp-elite-banner">
          <h3>{t("landingPageContent.features.mentorTitle")}</h3>
          <p>
            {t("landingPageContent.features.mentorDesc1")}
          </p>
          <p>{t("landingPageContent.features.mentorDesc2")}</p>
          <div style={{ marginTop: "20px", width: "100%", minHeight: "200px" }}>
            <img
              src={process.env.PUBLIC_URL + "/images/lp-feature-mentor.png"}
              alt={t("landingPageContent.features.mentorAlt")}
              loading="lazy"
              width="800"
              height="400"
              style={{
                maxWidth: "100%",
                height: "auto",
                borderRadius: "8px",
                border: "1px solid #444",
                display: "block",
              }}
            />
          </div>
        </div>
      </section>

      {/* SECTION 6: PRICING */}
      <section className="lp-section lp-section-alt" id="pricing">
        <h2 className="lp-section-title">{t("landingPageContent.pricing.title")}</h2>

        <div className="lp-pricing-toggle">
          <span
            style={{
              fontWeight: billingCycle === "monthly" ? "bold" : "normal",
            }}
          >
            {t("landingPageContent.pricing.monthly")}
          </span>
          <label className="lp-switch">
            <input
              type="checkbox"
              checked={billingCycle === "yearly"}
              onChange={toggleBilling}
            />
            <span className="lp-slider"></span>
          </label>
          <span
            style={{
              fontWeight: billingCycle === "yearly" ? "bold" : "normal",
            }}
          >
            {t("landingPageContent.pricing.yearly")}
          </span>
        </div>

        <div className="lp-pricing-grid">
          {/* FREE */}
          <div className="lp-card">
            <h3>{t("landingPageContent.pricing.free.name")}</h3>
            <div className="lp-price-box">
              <div className="lp-price-amount">{t("landingPageContent.pricing.free.price")}</div>
              <div className="lp-price-period">{t("landingPageContent.pricing.free.period")}</div>
            </div>
            <div className="lp-price-desc">{t("landingPageContent.pricing.free.desc")}</div>
            <ul className="lp-features-list">
              <li>
                <span className="lp-check">✓</span> {t("landingPageContent.pricing.free.feature1")}
              </li>
              <li>
                <span className="lp-check">✓</span> {t("landingPageContent.pricing.free.feature2")}
              </li>
            </ul>
            <button onClick={handleDemoClick} className="lp-btn-outline">
              {t("landingPageContent.pricing.free.button")}
            </button>
          </div>

          {/* BASIC */}
          <div className="lp-card">
            <h3>{t("landingPageContent.pricing.basic.name")}</h3>
            <div className="lp-price-box">
              <div className="lp-price-amount">
                {billingCycle === "yearly" ? t("landingPageContent.pricing.basic.priceYearly") : t("landingPageContent.pricing.basic.priceMonthly")}
              </div>
              <div className="lp-price-period">
                {billingCycle === "yearly" ? t("landingPageContent.pricing.basic.periodYearly") : t("landingPageContent.pricing.basic.periodMonthly")}
              </div>
            </div>
            <div className="lp-price-desc">{t("landingPageContent.pricing.basic.desc")}</div>
            <ul className="lp-features-list">
              <li>
                <span className="lp-check">✓</span> {t("landingPageContent.pricing.basic.feature1")}
              </li>
              <li>
                <span className="lp-check">✓</span> {t("landingPageContent.pricing.basic.feature2")}
              </li>
              <li>
                <span className="lp-check">✓</span> {t("landingPageContent.pricing.basic.feature3")}
              </li>
              <li>
                <span className="lp-check">✓</span> {t("landingPageContent.pricing.basic.feature4")}
              </li>
            </ul>
            <Link
              to="/manage-subscription?plan=basic"
              className="lp-btn-outline"
            >
              {t("landingPageContent.pricing.basic.button")}
            </Link>
          </div>

          {/* PRO */}
          <div className="lp-card lp-card-popular">
            <div className="lp-popular-tag">{t("landingPageContent.pricing.pro.popular")}</div>
            <h3>{t("landingPageContent.pricing.pro.name")}</h3>
            <div className="lp-price-box">
              <div className="lp-price-amount">
                {billingCycle === "yearly" ? t("landingPageContent.pricing.pro.priceYearly") : t("landingPageContent.pricing.pro.priceMonthly")}
              </div>
              <div className="lp-price-period">
                {billingCycle === "yearly" ? t("landingPageContent.pricing.pro.periodYearly") : t("landingPageContent.pricing.pro.periodMonthly")}
              </div>
            </div>
            <div className="lp-price-desc">{t("landingPageContent.pricing.pro.desc")}</div>
            <ul className="lp-features-list">
              <li>
                <span className="lp-check">✓</span> {t("landingPageContent.pricing.pro.feature1")}
              </li>
              <li>
                <span className="lp-check">✓</span> {t("landingPageContent.pricing.pro.feature2")}
              </li>
              <li>
                <span className="lp-check">✓</span> {t("landingPageContent.pricing.pro.feature3")}
              </li>
              <li>
                <span className="lp-check">✓</span> {t("landingPageContent.pricing.pro.feature4")}
              </li>
              <li>
                <span className="lp-check">✓</span> {t("landingPageContent.pricing.pro.feature5")}
              </li>
              <li>
                <span className="lp-check">✓</span> {t("landingPageContent.pricing.pro.feature6")}
              </li>
            </ul>
            <Link to="/manage-subscription?plan=pro" className="lp-btn-primary">
              {t("landingPageContent.pricing.pro.button")}
            </Link>
          </div>
        </div>

        {/* ELITE */}
        <div className="lp-elite-banner" style={{ marginTop: "40px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "20px",
            }}
          >
            <div style={{ textAlign: "left", flex: 1 }}>
              <h3 style={{ fontSize: "28px", margin: 0 }}>{t("landingPageContent.pricing.elite.name")}</h3>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "bold",
                  color: "#fff",
                  marginTop: "10px",
                }}
              >
                {billingCycle === "yearly" ? t("landingPageContent.pricing.elite.priceYearly") : t("landingPageContent.pricing.elite.priceMonthly")}
              </div>
              <p>{t("landingPageContent.pricing.elite.desc")}</p>
            </div>
            <div style={{ textAlign: "left", flex: 2 }}>
              <ul
                className="lp-features-list"
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  color: "#ddd",
                }}
              >
                <li>✓ {t("landingPageContent.pricing.elite.feature1")}</li>
                <li>✓ {t("landingPageContent.pricing.elite.feature2")}</li>
                <li>✓ {t("landingPageContent.pricing.elite.feature3")}</li>
                <li>✓ {t("landingPageContent.pricing.elite.feature4")}</li>
                <li>✓ {t("landingPageContent.pricing.elite.feature5")}</li>
                <li>✓ {t("landingPageContent.pricing.elite.feature6")}</li>
              </ul>
            </div>
            <div>
              <Link
                to="/manage-subscription?plan=elite"
                className="lp-elite-btn"
              >
                {t("landingPageContent.pricing.elite.button")}
              </Link>
            </div>
          </div>
        </div>

        <div
          style={{
            textAlign: "center",
            marginTop: "40px",
            color: "#666",
            fontSize: "14px",
          }}
        >
          {t("landingPageContent.pricing.guarantee")}
        </div>
      </section>

      {/* SECTION 8: FAQ */}
      <section className="lp-section">
        <h2 className="lp-section-title">{t("landingPageContent.faq.title")}</h2>
        <div className="lp-faq-grid">
          <div className="lp-faq-item">
            <h4>{t("landingPageContent.faq.q1")}</h4>
            <p>
              {t("landingPageContent.faq.a1")}
            </p>
          </div>
          <div className="lp-faq-item">
            <h4>{t("landingPageContent.faq.q2")}</h4>
            <p>
              {t("landingPageContent.faq.a2")}
            </p>
          </div>
          <div className="lp-faq-item">
            <h4>{t("landingPageContent.faq.q3")}</h4>
            <p>
              {t("landingPageContent.faq.a3")}
            </p>
          </div>
          <div className="lp-faq-item">
            <h4>{t("landingPageContent.faq.q4")}</h4>
            <p>
              {t("landingPageContent.faq.a4")}
            </p>
          </div>
          <div className="lp-faq-item">
            <h4>{t("landingPageContent.faq.q5")}</h4>
            <p>
              {t("landingPageContent.faq.a5")}
            </p>
          </div>
          <div className="lp-faq-item">
            <h4>{t("landingPageContent.faq.q6")}</h4>
            <p>
              {t("landingPageContent.faq.a6")}
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 9: FINAL CTA */}
      <section className="lp-section lp-section-dark lp-final-cta">
        <h2>{t("landingPageContent.finalCta.title")}</h2>
        <p style={{ fontSize: "18px", marginBottom: "30px", opacity: 0.9 }}>
          {t("landingPageContent.finalCta.subtitle")}
        </p>
        <button
          onClick={handleDemoClick}
          className="lp-cta-button"
          style={{ background: "white", color: "var(--primary)" }}
        >
          {t("landingPageContent.finalCta.button")}
        </button>
        <div className="lp-guarantee">
          <ul className="lp-guarantee-list">
            <li>{t("landingPageContent.finalCta.guarantee1")}</li>
            <li>{t("landingPageContent.finalCta.guarantee2")}</li>
            <li>{t("landingPageContent.finalCta.guarantee3")}</li>
          </ul>
        </div>
        <div style={{ marginTop: "30px", fontSize: "14px", opacity: 0.7 }}>
          {t("landingPageContent.finalCta.support")}
        </div>
      </section>

      <footer
        style={{
          textAlign: "center",
          padding: "40px",
          color: "#999",
          fontSize: "14px",
          borderTop: "1px solid #eee",
        }}
      >
        {t("landingPageContent.footer.copyright").replace("{year}", new Date().getFullYear().toString())}
      </footer>
    </div>
  );
};

export default LandingPage;
